/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Wizard Product Offerings Sagas
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.10.0
 */
import { createElement } from 'react';

import isEqual from 'lodash/isEqual';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';

import { ETextBlockStatus } from '../../components/common/TextBlockStatus/textBlockStatusTypes';
import { tableResetAction } from '../../components/Table/actions/tableActions';
import { GET_BASKET_COUNT_SUCCESSFUL } from '../../components/Table/constants/actionConstants';
import getActionConflictsParsed from '../../components/Table/helpers/getActionConflictsParsed';
import type { IActionConflictsParsed } from '../../components/Table/helpers/getActionConflictsParsed';
import { makeSelectorTableCount } from '../../components/Table/selectors/tableSelectors';
import { TableNameType } from '../../components/Table/types/tableTypes';
import { HttpStatus } from '../../modules/app/appConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardProductOfferings,
    makeSelectWizardTranslation,
} from '../../modules/internationalization/intSelectors';
import type { IWizardProductOfferingsTranslation } from '../../modules/internationalization/types';
import type ICommonTranslation from '../../modules/internationalization/types/common';
import ModalErrorCommon from '../../modules/modal/components/ModalError';
import { modalOpen } from '../../modules/modal/modalActions';
import type { IModalRenderProps } from '../../modules/modal/types/modalTypes';
import { getSubscriberRegionsSaga } from '../../modules/subscribers/subscribersRegionsSaga';
import { wizardReportValidateSaga } from '../../modules/wizard/components/WizardReport/wizardReportSagas';
import { wizardNextAction, wizardStorageSaveStepAction } from '../../modules/wizard/wizardActions';
import {
    WIZARD_BACK_FAILED,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_ACTION,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
} from '../../modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '../../modules/wizard/wizardSagas';
import { makeSelectWizardBasketSnapshotId, makeSelectWizardStepName } from '../../modules/wizard/wizardSelectors';
import {
    TWizardMetaSuccessAction,
    TWizardNextSuccessAction,
    TWizardStartAction,
    TWizardStartSuccessAction,
} from '../../modules/wizard/wizardTypes';
import { stringFormatParams } from '../../utils/stringFormat';
import { makeSelectAuthoritiesIsServiceUser } from '../PageAuth/selectors/authSelectors';
import type { IAuthRegion } from '../PageAuth/types/authTypes';
import { EProductsPageFrom } from '../PageProducts/tariffsAndServicesConstants';
import { makeSelectProductsPageFrom } from '../PageProducts/tariffsAndServicesSelectors';
import {
    productOfferingsClearAction,
    productOfferingsSetParamsAction,
    productOfferingsSetStepValidAction,
    productOfferingsStartValidateAction,
} from './wizardProductOfferingsActions';
import {
    EWizardProductOfferingsSteps,
    PRODUCT_OFFERING_CREATE_INQUIRY_START,
    PRODUCT_OFFERING_GET_SUBSCRIBERS_CHECK_LIST_ACTION,
    PRODUCT_OFFERING_HANDLE_NEXT,
    PRODUCT_OFFERING_SAVE_STORAGE,
    PRODUCT_OFFERING_SET_MEGAPOWER,
    PRODUCT_OFFERING_SET_PARAMS,
    PRODUCT_OFFERING_START_VALIDATE,
    PRODUCT_SUBSCRIBERS_LIST_INIT_START,
} from './wizardProductOfferingsConstants';
import {
    makeSelectProductOfferingsDateFrom,
    makeSelectProductOfferingsDateTo,
    makeSelectProductOfferingsInitialMegaPowers,
    makeSelectProductOfferingsIsDisconnect,
    makeSelectProductOfferingsIsTariff,
    makeSelectProductOfferingsListRemovedSubscribers,
    makeSelectProductOfferingsListToConnect,
    makeSelectProductOfferingsMaxSubsForProductActivation,
    makeSelectProductOfferingsMegaPowers,
    makeSelectProductOfferingsRegionId,
    makeSelectProductOfferingsSubsCount,
    makeSelectProductOfferingsRatePlanState,
    makeSelectProductOfferingsServicesState,
} from './wizardProductOfferingsSelectors';
import {
    openModalWithSuggestInquiry,
    productOfferingsCreateInquirySaga,
    productOfferingsGetSubscribersCheckListSaga,
    productOfferingsStepConfirmationHandleNextSaga,
    productOfferingsStepConfirmationInitSaga,
    productOfferingsStepConfirmationNextSuccessSaga,
    productOfferingsStepConfirmationUpdateStorageSaga,
} from './wizardProductOfferingsStepConfirmationSaga';
import {
    productOfferingsStepSelectDateInitSaga,
    productOfferingsStepSelectDateNextSuccessSaga,
    productOfferingsStepSelectDateUpdateStorageSaga,
} from './wizardProductOfferingsStepSelectDateSaga';
import {
    productOfferingsStepSelectMegaPowersNextSuccessSaga,
    productOfferingsStepSelectMegaPowersUpdateMegaPowers,
    wizardProductOfferingsStepSelectMegaPowersInitSaga,
} from './wizardProductOfferingsStepSelectMegaPowersSaga';
import {
    productOfferingsStepSelectSubscribersHandleNextSaga,
    productOfferingsStepSelectSubscribersInitSaga,
    productSubscribersInitListSaga,
    productSubscribersSetSubsCountSaga,
} from './wizardProductOfferingsStepSelectSubscribersSaga';
import { EActionConflictTypes } from './wizardProductOfferingsTypes';
import type {
    IProductOfferingsSubscriberActivationItem,
    IWizardProductOfferingsLocalStorageData,
    IWizardProductOfferingsStartData,
} from './wizardProductOfferingsTypes';

/*
 * -------------------------------------------
 *           INITIALIZING
 * -------------------------------------------
 */

export function* productOfferingsWizardStartSaga(action: TWizardStartAction): SagaIterator {
    // getting productsOfferingIds
    const startData = action.payload?.data as IWizardProductOfferingsStartData;

    // clear previous state
    yield put(productOfferingsClearAction());
    yield put(tableResetAction({ tableName: TableNameType.productSelectSubscribers }));

    const startSuccessAction: TWizardStartSuccessAction = yield take(WIZARD_START_SUCCESS);
    const isTariff = startSuccessAction.payload.meta.step.isTariff;

    if (startData) {
        yield put(
            wizardStorageSaveStepAction({
                params: { ...startData, isTariff },
            }),
        );
    }
}

const mapInitializeSaga: { [key: string]: any } = {
    [EWizardProductOfferingsSteps.StepSelectSubscribers]: productOfferingsStepSelectSubscribersInitSaga,
    [EWizardProductOfferingsSteps.StepSelectMegaPowers]: wizardProductOfferingsStepSelectMegaPowersInitSaga,
    [EWizardProductOfferingsSteps.StepSelectDate]: productOfferingsStepSelectDateInitSaga,
    [EWizardProductOfferingsSteps.StepConfirmation]: productOfferingsStepConfirmationInitSaga,
};

export function* productOfferingsWizardMetaSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    yield call(productOfferingsLoadDataFromStorageSaga);
    yield call(productOfferingSetRegionSaga);

    const initializeSaga = mapInitializeSaga[stepName];
    if (initializeSaga) {
        yield call(initializeSaga, action);
    }

    yield put(productOfferingsStartValidateAction());
}

/*
 * -------------------------------------------
 *       LOAD DATA FROM STORAGE
 * -------------------------------------------
 */

export function* productOfferingsLoadDataFromStorageSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const localStorageParams: IWizardProductOfferingsLocalStorageData = localStorageState[basketSnapshotId];

    /**
     * Если обновили страницу обновим всю информацию, которая у нас есть
     */
    if (localStorageParams) {
        yield put(
            productOfferingsSetParamsAction({
                dateFrom: localStorageParams?.dateFrom,
                dateTo: localStorageParams?.dateTo,
                isDisconnect: localStorageParams.isDisconnect,
                isTariff: localStorageParams.isTariff,
                regionId: localStorageParams.regionId,
                removedSubscribers: localStorageParams.removedSubscribers,
                ratePlan: localStorageParams?.ratePlan,
                services: localStorageParams?.services,
            }),
        );
    }
    yield call(productOfferingsUpdateStorageSaga);
}

export function* productOfferingsValidateSaga(): SagaIterator {
    let isStepValid = true;
    let validateError = '';
    const stepName = yield select(makeSelectWizardStepName());
    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());
    const translationProductOfferings: IWizardProductOfferingsTranslation = yield select(
        makeSelectTranslationWizardProductOfferings(),
    );
    const subsCount = yield select(makeSelectProductOfferingsSubsCount());
    const maxSubs = yield select(makeSelectProductOfferingsMaxSubsForProductActivation());
    const isTariff = yield select(makeSelectProductOfferingsIsTariff());
    const ratePlan = yield select(makeSelectProductOfferingsRatePlanState());
    const services = yield select(makeSelectProductOfferingsServicesState());
    const subscriberActivationList: IProductOfferingsSubscriberActivationItem[] = yield select(
        makeSelectProductOfferingsListToConnect(),
    );
    const removedSubscribers: string[] = yield select(makeSelectProductOfferingsListRemovedSubscribers());

    switch (stepName) {
        case EWizardProductOfferingsSteps.StepSelectSubscribers:
            const totalSubscribers = yield select(makeSelectorTableCount());
            let subsLimit = 0;
            let maxCount = 0;

            // проверяем ограничение для limitConnections
            if (isTariff) {
                subsLimit = (ratePlan?.limitConnections ?? 0) - (ratePlan?.connected ?? 0)
            } else {
                if ((services?.length ?? 0) === 1) {
                    subsLimit = (services[0]?.limitConnections ?? 0) - (services[0]?.connected ?? 0)
                } else {
                    subsLimit = 1;
                }
            }

            // выбираем наименьшее ограничение
            if (subsLimit > 0) {
                maxCount = Math.min(maxSubs, subsLimit);
            } else {
                maxCount = maxSubs;
            }

            // если из-за limitConnection у нас изменился maxCount,
            // то апдейтим значение maxSubsForProductActivation в хранилище
            if (maxSubs !== maxCount) {
                yield put(productOfferingsSetParamsAction({
                    maxSubsForProductActivation: maxCount,
                }));
                yield call(productOfferingsUpdateStorageSaga);
            }

            isStepValid = subsCount > 0 && subsCount <= maxCount && totalSubscribers > 0;
            validateError = isStepValid
                ? ''
                : stringFormatParams(translationProductOfferings.validateErrors.selectSubscribers, { maxCount });
            break;
        case EWizardProductOfferingsSteps.StepSelectDate:
            const dateFrom = yield select(makeSelectProductOfferingsDateFrom());
            const dateTo = yield select(makeSelectProductOfferingsDateTo());
            const isDisconnect = yield select(makeSelectProductOfferingsIsDisconnect());
            isStepValid = isDisconnect ? !!dateTo : !!dateFrom;
            validateError = isStepValid ? '' : translationProductOfferings.validateErrors.selectDate;
            break;
        case EWizardProductOfferingsSteps.StepSelectMegaPowers:
            const initialMegaPowers = yield select(makeSelectProductOfferingsInitialMegaPowers());
            const megaPowers = yield select(makeSelectProductOfferingsMegaPowers());

            isStepValid = initialMegaPowers.length > 0;
            validateError =
                initialMegaPowers.length > 0
                    ? ''
                    : translationProductOfferings.MegaPowersConfigurerStep.megaPowersListError;

            const pageFrom = yield select(makeSelectProductsPageFrom());

            if (pageFrom === EProductsPageFrom.SUBSCRIBER_ID_CHANGE_MEGA_POWERS) {
                isStepValid = !isEqual(initialMegaPowers, megaPowers);
            }

            break;
        case EWizardProductOfferingsSteps.StepConfirmation:
            // служебный пользователь может дойти до конца визрда, но завершить не может
            const isServiceUser: boolean = yield select(makeSelectAuthoritiesIsServiceUser());
            if (isServiceUser) {
                const commonTranslation: ICommonTranslation = yield select(makeSelectTranslationCommon());
                validateError = commonTranslation.serviceUser.tooltip;
                isStepValid = false;
                break;
            }

            // проверяем количество выбранных абонентов
            if (subsCount < 1 || subsCount === removedSubscribers?.length) {
                validateError = translationProductOfferings.validateErrors.selectOneSubscribers;
                isStepValid = false;
                break;
            }

            // проверяем, что возможно подключение услуг хотя бы одному абоненту
            const successSubscriberActivation = subscriberActivationList
                .filter((item) => !removedSubscribers?.find((id: string) => id === item.id))
                .map((item: IProductOfferingsSubscriberActivationItem) =>
                    getActionConflictsParsed(item.actionConflicts || [], {
                        [EActionConflictTypes.ERROR]: translationCommon.corporateTable.actionConflictsTitle.error,
                        [EActionConflictTypes.WARNING]: translationCommon.corporateTable.actionConflictsTitle.warning,
                    }),
                )
                .filter((item: IActionConflictsParsed<ETextBlockStatus>) => item.status !== ETextBlockStatus.ERROR);
            if (successSubscriberActivation.length < 1) {
                validateError = translationProductOfferings.validateErrors.selectSuccessSubscribers;
                isStepValid = false;
                break;
            }

            // проверяем настройки доставки
            const isReportValid = yield call(wizardReportValidateSaga);
            isStepValid = isReportValid;
            validateError = isStepValid ? '' : translationProductOfferings.validateErrors.fillNotify;
            break;
    }
    yield put(
        productOfferingsSetStepValidAction({
            isStepValid,
            validateError,
        }),
    );
}

/*
 * -------------------------------------------
 *       SEND DATA ON NEXT STEP
 * -------------------------------------------
 */

const mapProductOfferingsHandleNextSaga: { [key: string]: any } = {
    [EWizardProductOfferingsSteps.StepSelectSubscribers]: productOfferingsStepSelectSubscribersHandleNextSaga,
    [EWizardProductOfferingsSteps.StepConfirmation]: productOfferingsStepConfirmationHandleNextSaga,
};

export function* productOfferingsHandleNextSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    const handleNextSaga = mapProductOfferingsHandleNextSaga[stepName];

    if (stepName === EWizardProductOfferingsSteps.StepSelectMegaPowers) {
        yield call(productOfferingsStepSelectMegaPowersUpdateMegaPowers);
    }

    if (handleNextSaga) {
        const dataForSend = yield call(handleNextSaga);

        yield put(wizardNextAction({ ...dataForSend }));
    } else {
        yield put(
            wizardNextAction({
                // default
            }),
        );
    }

    yield call(productOfferingsUpdateStorageSaga);
}

/*
 * -------------------------------------------
 *       NEXT SUCCESS
 * -------------------------------------------
 */

const mapProductOfferingsNextSuccessSaga: { [key: string]: (action: TWizardNextSuccessAction) => SagaIterator } = {
    [EWizardProductOfferingsSteps.StepSelectDate]: productOfferingsStepSelectDateNextSuccessSaga,
    [EWizardProductOfferingsSteps.StepSelectMegaPowers]: productOfferingsStepSelectMegaPowersNextSuccessSaga,
    [EWizardProductOfferingsSteps.StepConfirmation]: productOfferingsStepConfirmationNextSuccessSaga,
};

export function* productOfferingsNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    const nextSuccessSaga = mapProductOfferingsNextSuccessSaga[stepName];
    if (nextSuccessSaga) {
        yield call(nextSuccessSaga, action);
    }

    yield call(productOfferingsUpdateStorageSaga);
}

/*
 * -------------------------------------------
 *       SAVE DATA TO STORAGE
 * -------------------------------------------
 */

const mapProductOfferingsUpdateToStorageSaga: { [key: string]: any } = {
    [EWizardProductOfferingsSteps.StepSelectSubscribers]: productOfferingsStepConfirmationUpdateStorageSaga,
    [EWizardProductOfferingsSteps.StepSelectDate]: productOfferingsStepSelectDateUpdateStorageSaga,
    [EWizardProductOfferingsSteps.StepSelectMegaPowers]: productOfferingsStepSelectDateUpdateStorageSaga,
    [EWizardProductOfferingsSteps.StepConfirmation]: productOfferingsStepConfirmationUpdateStorageSaga,
};

export function* productOfferingsUpdateStorageSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    const saveDataToStorageSaga = mapProductOfferingsUpdateToStorageSaga[stepName];
    if (saveDataToStorageSaga) {
        yield call(saveDataToStorageSaga);
    }
}

export function* productOfferingsFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        if (errorCode === 'order.activationError') {
            /**
             * Обработка кейса с созданием обращения
             */
            yield call(openModalWithSuggestInquiry);
        } else {
            /**
             * В ином случае - протухший мастер
             */
            yield call(wizardModalTimeoutSaga);
        }
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        const translationProductOfferings = yield select(makeSelectTranslationWizardProductOfferings());
        const wizardTranslation = yield select(makeSelectWizardTranslation());
        title =
            translationProductOfferings.error.codes[errorCode] ||
            translationCommon.error.codes[errorCode] ||
            wizardTranslation.wizardModalError.default;
    }

    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    title,
                    translation: translationCommon.modal,
                }),
        }),
    );
}

/**
 * Если у абонента доступно несколько регионов, то выбирается region на основе regionId
 * Если region !== undefined, то показывается инфо-сообщение о выбранном регионе у абонента
 */
export function* productOfferingSetRegionSaga(): SagaIterator {
    try {
        const regions: IAuthRegion[] = yield call(getSubscriberRegionsSaga);
        if (regions.length > 1) {
            const regionId: number = yield select(makeSelectProductOfferingsRegionId());
            const region = regions.find((r: IAuthRegion) => r.id === regionId);
            yield put(productOfferingsSetParamsAction({ region }));
        } else {
            yield put(productOfferingsSetParamsAction({ region: undefined }));
        }
    } catch (e) {
        yield put(productOfferingsSetParamsAction({ region: undefined }));
    }
}

export function* wizardProductOfferingsSagas(): SagaIterator {
    yield all([
        // init
        takeEvery(WIZARD_START_ACTION, productOfferingsWizardStartSaga),
        takeEvery(WIZARD_META_SUCCESS, productOfferingsWizardMetaSaga),
        takeEvery(PRODUCT_SUBSCRIBERS_LIST_INIT_START, productSubscribersInitListSaga),
        takeEvery(GET_BASKET_COUNT_SUCCESSFUL, productSubscribersSetSubsCountSaga),
        // failed
        takeEvery(
            [WIZARD_BACK_FAILED, WIZARD_NEXT_FAILED, WIZARD_START_FAILED, WIZARD_RESTORE_FAILED],
            productOfferingsFailedSaga,
        ),
        // next
        takeEvery(PRODUCT_OFFERING_HANDLE_NEXT, productOfferingsHandleNextSaga),
        takeEvery(WIZARD_NEXT_SUCCESS, productOfferingsNextSuccessSaga),
        // validate
        takeEvery(
            [PRODUCT_OFFERING_START_VALIDATE, PRODUCT_OFFERING_SET_PARAMS, PRODUCT_OFFERING_SET_MEGAPOWER],
            productOfferingsValidateSaga,
        ),
        // storage
        takeEvery(PRODUCT_OFFERING_SAVE_STORAGE, productOfferingsUpdateStorageSaga),

        takeEvery(PRODUCT_OFFERING_GET_SUBSCRIBERS_CHECK_LIST_ACTION, productOfferingsGetSubscribersCheckListSaga),
        takeEvery(PRODUCT_OFFERING_CREATE_INQUIRY_START, productOfferingsCreateInquirySaga),
    ]);
}
